import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_END_POINTS, BASE_URL } from "../ApiEndPoints";
import axios from "axios";
import globalFunction from "../../utils/globalFunction";

const handleUnauthorizedError = (message) => {
  if (
    message === "Unauthorized" ||
    message?.toLowerCase().includes("user not authorized") ||
    message?.toLowerCase().includes("token has expired") ||
    message?.toLowerCase().includes("jwt expire")
  ) {
    globalFunction.logoutFunction();
  }
};

export const adminGetAllCoursesAPI = createAsyncThunk(
  "adminGetAllCoursesAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_END_POINTS.getAllCourses, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credentials.token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch courses"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const instructorGetAllCoursesAPI = createAsyncThunk(
  "instructorGetAllCoursesAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_END_POINTS.instructorCourses, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credentials.token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch courses"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getAllCourseCategoriesAPI = createAsyncThunk(
  "getAllCourseCategoriesAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_END_POINTS.getAllCategories, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credentials.token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch courses"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getAllInstructorsAPI = createAsyncThunk(
  "getAllInstructorsAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_END_POINTS.getAllInstructors, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credentials.token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const courseAssignmentAPI = createAsyncThunk(
  "courseAssignmentAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + `course/${credentials.course_id}/assignInstructor`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const adminGetAllStudentsAPI = createAsyncThunk(
  "adminGetAllStudentsAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_END_POINTS.getAllStudents, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credentials.token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch students"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const instructorGetAllStudentsAPI = createAsyncThunk(
  "instructorGetAllStudentsAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_END_POINTS.instructorStudents, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credentials.token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch students"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getStudentByIdAPI = createAsyncThunk(
  "admin/getStudentByIdAPI",
  async ({ token, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_END_POINTS.getUserById}/${userId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch student details"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

// Thunk for adding experience
export const addExperienceAPI = createAsyncThunk(
  "admin/addExperienceAPI",
  async ({ token, payload }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_END_POINTS.addExperience, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to add experience"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

// Thunk for updating experience
export const updateExperienceAPI = createAsyncThunk(
  "admin/updateExperienceAPI",
  async ({ token, payload }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        API_END_POINTS.updateExperience,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to update experience"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

// Thunk for deleting experience
export const deleteExperienceAPI = createAsyncThunk(
  "admin/deleteExperienceAPI",
  async ({ token, experienceId, userId, role }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        role === "student"
          ? API_END_POINTS.studentDeleteExperience
          : API_END_POINTS.deleteExperience,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          data: { userId, experienceId }, // Send userId and experienceId in the body
        }
      );
      return { experienceId, ...response.data };
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to delete experience"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

// export const deleteExperienceAPI = createAsyncThunk(
//   "admin/deleteExperienceAPI",
//   async ({ token, experienceId }, { rejectWithValue }) => {
//     try {
//       const response = await axios.delete(`${API_END_POINTS.deleteExperience}?experience_id=${experienceId}`, {
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//         },
//       });
//       return { experienceId, ...response.data };
//     } catch (error) {
//       if (error.response) {
//         return rejectWithValue(
//           error.response?.data?.message || "Failed to delete experience"
//         );
//       }
//       return rejectWithValue("Something went wrong!");
//     }
//   }
// );

// Thunk for adding education
export const addEducationAPI = createAsyncThunk(
  "admin/addEducationAPI",
  async ({ token, payload, role }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        role === "student"
          ? API_END_POINTS.studentAddEducation
          : API_END_POINTS.addEducation,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add education"
      );
    }
  }
);
export const clearNotificationsAPI = createAsyncThunk(
  "notifications/clearNotifications",
  async (token, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_END_POINTS.clearNotifications,
        {}, // POST body empty
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Thunk for updating education
export const updateEducationAPI = createAsyncThunk(
  "admin/updateEducationAPI",
  async ({ token, payload, role }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        role === "student"
          ? API_END_POINTS.studentUpdateEducation
          : API_END_POINTS.updateEducation,

        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update education"
      );
    }
  }
);

// Thunk for deleting education
export const deleteEducationAPI = createAsyncThunk(
  "admin/deleteEducationAPI",
  async ({ token, educationId, userId, role }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        role === "student"
          ? API_END_POINTS.studentDeleteEducation
          : API_END_POINTS.deleteEducation,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          data: { userId, educationId },
        }
      );
      return { educationId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete education"
      );
    }
  }
);

export const getAllOrganizationRequestsAPI = createAsyncThunk(
  "getAllOrganizationRequestsAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.getAllOrganizationRequests,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to fetch organization requests"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getEditProfileAPI = createAsyncThunk(
  "getEditProfileAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.userProfileByAdmin + `/${credentials.userId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to fetch organization requests"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getDeactivatedUsersAPI = createAsyncThunk(
  "getDeactivatedUsersAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_END_POINTS.getDeactivatedUsers, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credentials.token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch deactivated users"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const updateInstructorProfileAPI = createAsyncThunk(
  "updateInstructorProfileAPI",
  async (credentials, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append("date_of_birth", credentials.payload.date_of_birth);
    formData.append("email", credentials.payload.email);
    formData.append("full_name", credentials.payload.full_name);
    formData.append("password", credentials.payload.password);
    formData.append("phone_number", credentials.payload.phone_number);
    formData.append("plan", credentials.payload.plan);
    formData.append("profile_picture", credentials.payload.profileImage);

    try {
      const response = await axios.put(
        API_END_POINTS.userProfileUpdate + `/${credentials.userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          // error.response?.data?.message || "Failed to fetch deactivated users"

          error.response?.data?.message ||
            "Failed to fetch organization requests"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const updatePasswordAPI = createAsyncThunk(
  "admin/updatePassword",
  async ({ token, userId, payload }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_END_POINTS.updatePassword}/${userId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      handleUnauthorizedError(error?.response?.data?.message);
      return rejectWithValue(
        error.response?.data || { message: "Failed to update password" }
      );
    }
  }
);

export const activateUserAPI = createAsyncThunk(
  "activateUserAPI",
  async ({ token, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_END_POINTS.activateUser}/${userId}/activate`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return { userId, ...response.data };
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response?.data?.message || "Failed to activate user"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);
export const instructorAddExperienceAPIForEditProfile = createAsyncThunk(
  "instructorAddExperienceAPIForEditProfile",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        credentials.role === "student"
          ? API_END_POINTS.studentAddExperience
          : API_END_POINTS.addExperience,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);
export const instructorUpdateExperienceAPIForEditProfile = createAsyncThunk(
  "instructorUpdateExperienceAPIForEditProfile",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        credentials.role === "student"
          ? API_END_POINTS.studentUpdateExperience
          : API_END_POINTS.updateExperience,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);
export const deactivateUserAPI = createAsyncThunk(
  "deactivateUserAPI",
  async ({ token, userId, payload }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_END_POINTS.deactivateUser}/${userId}/deactivate`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return { userId, ...response.data };
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response?.data?.message || "Failed to deactivate user"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// Thunk for fetching pending course requests
export const getPendingApprovalCoursesAPI = createAsyncThunk(
  "getPendingApprovalCoursesAPI",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.pendingApprovalCoursesAdmin,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch pending courses"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

// Thunk for approving a course
export const approveCourseAPI = createAsyncThunk(
  "approveCourseAPI",
  async ({ token, courseId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.approveCourse.replace(":courseId", courseId),
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response?.data?.message || "Failed to approve course"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

// Thunk for rejecting a course
export const rejectCourseAPI = createAsyncThunk(
  "rejectCourseAPI",
  async ({ token, courseId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.rejectCourse.replace(":courseId", courseId),
        { reason },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response?.data?.message || "Failed to reject course"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

export const getPendingUnpublishCoursesAPI = createAsyncThunk(
  "getPendingUnpublishCoursesAPI",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.pendingUnpublishCoursesAdmin,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to fetch pending unpublish courses"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

export const approveUnpublishCourseAPI = createAsyncThunk(
  "approveUnpublishCourseAPI",
  async ({ token, courseId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.approveUnpublishCourse.replace(":courseId", courseId),
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve unpublish request"
      );
    }
  }
);

export const rejectUnpublishCourseAPI = createAsyncThunk(
  "rejectUnpublishCourseAPI",
  async ({ token, courseId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.rejectUnpublishCourse.replace(":courseId", courseId),
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject unpublish request"
      );
    }
  }
);

// Delete Review Thunk
export const deleteReviewAPI = createAsyncThunk(
  "reviews/deleteReview",
  async ({ reviewId, token }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${API_END_POINTS.deleteReview}/${reviewId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return {
        reviewId,
        message: response?.data || "Review deleted successfully",
      };
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data || "Failed to delete review"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

export const getAllCertificateByAdminAPI = createAsyncThunk(
  "getAllCertificateByAdminAPI",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.getAllCertificateByAdmin,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch certificates"
        );
      }
      return rejectWithValue("Something went wrong!");
    }
  }
);

export const adminApprovedCertificateAPI = createAsyncThunk(
  "certificate/adminApprovedCertificateAPI",
  async ({ token, certificateId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_END_POINTS.adminApprovedCertificate}/${certificateId}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return { certificateId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const adminRejectCertificateAPI = createAsyncThunk(
  "certificate/adminRejectCertificateAPI",
  async ({ token, certificateId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_END_POINTS.adminRejectCertificate}/${certificateId}/reject`,
        { reason: reason },
        {
          "Content-Type": "application/json",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return { certificateId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const adminSlice = createSlice({
  name: "admin",
  initialState: {
    students: {
      data: [],
      totalCount: 0,
      loading: false,
      error: null,
    },
    courses: {
      data: [],
      totalCount: 0,
      loading: false,
      error: null,
    },
    instructors: {
      data: [],
      loading: false,
      error: null,
    },
    selectedStudent: {
      profile: null,
      experiences: [],
      educations: [],
      courses: [],
      loading: false,
      error: null,
    },
    metrics: {
      activeStudents: 0,
      newEnrollments: 0,
      studentProgress: 0,
    },
    organizationRequests: {
      data: [],
      totalCount: 0,
      loading: false,
      error: null,
    },
    deactivatedUsers: {
      data: [],
      totalCount: 0,
      loading: false,
      error: null,
    },
    deactivationStatus: {
      loading: false,
      error: null,
      success: false,
    },
    status: "idle",
    error: null,
  },
  reducers: {
    resetError(state) {
      state.students.error = null;
      state.courses.error = null;
      state.instructors.error = null;
      state.selectedStudent.error = null;
      state.organizationRequests.error = null;
      state.deactivatedUsers.error = null;
      state.deactivationStatus.error = null;
      state.error = null;
    },
    resetSelectedStudent(state) {
      state.selectedStudent = {
        profile: null,
        experiences: [],
        educations: [],
        courses: [],
        loading: false,
        error: null,
      };
    },
    resetDeactivationStatus(state) {
      state.deactivationStatus = {
        loading: false,
        error: null,
        success: false,
      };
    },
  },

  extraReducers: (builder) => {
    // Handle getPendingApprovalCoursesAPI
    builder.addCase(getPendingApprovalCoursesAPI.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPendingApprovalCoursesAPI.fulfilled, (state, action) => {
      state.loading = false;
      state.pendingCourses = action.payload.data.details || [];
    });
    builder.addCase(getPendingApprovalCoursesAPI.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Handle approveCourseAPI
    builder.addCase(approveCourseAPI.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(approveCourseAPI.fulfilled, (state, action) => {
      state.loading = false;
      state.pendingCourses = state.pendingCourses.map((course) =>
        course.id === action.meta.arg.courseId
          ? { ...course, status: "published" }
          : course
      );
    });
    builder.addCase(approveCourseAPI.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Handle rejectCourseAPI
    builder.addCase(rejectCourseAPI.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(rejectCourseAPI.fulfilled, (state, action) => {
      state.loading = false;
      state.pendingCourses = state.pendingCourses.map((course) =>
        course.id === action.meta.arg.courseId
          ? { ...course, status: "declined" }
          : course
      );
    });
    builder.addCase(rejectCourseAPI.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder
      .addCase(deactivateUserAPI.pending, (state) => {
        state.deactivationStatus.loading = true;
        state.deactivationStatus.error = null;
        state.deactivationStatus.success = false;
      })
      .addCase(deactivateUserAPI.fulfilled, (state, action) => {
        state.deactivationStatus.loading = false;
        state.deactivationStatus.success = true;
        if (state.selectedStudent.profile?.id === action.payload.userId) {
          state.selectedStudent.profile.status = "deactivated";
        }
      })
      .addCase(deactivateUserAPI.rejected, (state, action) => {
        state.deactivationStatus.loading = false;
        state.deactivationStatus.error = action.payload;
        state.deactivationStatus.success = false;
      });

    builder
      .addCase(getAllOrganizationRequestsAPI.pending, (state) => {
        state.organizationRequests.loading = true;
        state.organizationRequests.error = null;
      })
      .addCase(getAllOrganizationRequestsAPI.fulfilled, (state, action) => {
        state.organizationRequests.loading = false;
        state.organizationRequests.data = action.payload.data || [];
        state.organizationRequests.totalCount = action.payload.data.length || 0;
      })
      .addCase(getAllOrganizationRequestsAPI.rejected, (state, action) => {
        state.organizationRequests.loading = false;
        state.organizationRequests.error = action.payload;
      });

    builder
      .addCase(getDeactivatedUsersAPI.pending, (state) => {
        state.deactivatedUsers.loading = true;
        state.deactivatedUsers.error = null;
      })
      .addCase(getDeactivatedUsersAPI.fulfilled, (state, action) => {
        state.deactivatedUsers.loading = false;
        state.deactivatedUsers.data = action.payload.data || [];
        state.deactivatedUsers.totalCount = action.payload.data.length || 0;
      })
      .addCase(getDeactivatedUsersAPI.rejected, (state, action) => {
        state.deactivatedUsers.loading = false;
        state.deactivatedUsers.error = action.payload;
      });

    // Handle activateUserAPI
    builder
      .addCase(activateUserAPI.pending, (state) => {
        state.deactivatedUsers.loading = true;
        state.deactivatedUsers.error = null;
      })
      .addCase(activateUserAPI.fulfilled, (state, action) => {
        state.deactivatedUsers.loading = false;
        state.deactivatedUsers.data = state.deactivatedUsers.data.filter(
          (user) => user.userId !== action.payload.userId
        );
        state.deactivatedUsers.totalCount = state.deactivatedUsers.data.length;
      })
      .addCase(activateUserAPI.rejected, (state, action) => {
        state.deactivatedUsers.loading = false;
        state.deactivatedUsers.error = action.payload;
      });

    // Handle getStudentByIdAPI
    builder
      .addCase(getStudentByIdAPI.pending, (state) => {
        state.loading = true;
        state.selectedStudent.error = null;
      })
      .addCase(getStudentByIdAPI.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        state.selectedStudent = {
          profile: {
            id: action.payload.data.id,
            profile_image: action.payload.data.profile_picture,
            // ? `${
            //     API_END_POINTS.baseURL
            //   }/${action.payload.data.profile_picture.replace(/\\/g, "/")}`
            // : null,
            full_name: action.payload.data.full_name,
            email: action.payload.data.email,
            phone_number: action.payload.data.phone_number,
            date_of_birth: action.payload.data.date_of_birth,
            role: action.payload.data.role?.name || "Not Provided",
            status: action.payload.data.status,
            location: null,
          },
          experiences: action.payload.data.experiences || [],
          educations: action.payload.data.educations || [],
          courses: [],
        };
      })

      .addCase(getStudentByIdAPI.rejected, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.error = action.payload;
      });

    // Handle addExperienceAPI
    builder
      .addCase(addExperienceAPI.pending, (state) => {
        state.selectedStudent.loading = true;
        state.selectedStudent.error = null;
      })
      .addCase(addExperienceAPI.fulfilled, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.experiences = [
          action.payload.data,
          ...state.selectedStudent.experiences,
        ];
      })
      .addCase(addExperienceAPI.rejected, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.error = action.payload;
      });

    // Handle updateExperienceAPI
    builder
      .addCase(updateExperienceAPI.pending, (state) => {
        state.selectedStudent.loading = true;
        state.selectedStudent.error = null;
      })
      .addCase(updateExperienceAPI.fulfilled, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.experiences =
          state.selectedStudent.experiences.map((exp) =>
            exp.id === action.payload.data.id ? action.payload.data : exp
          );
      })
      .addCase(updateExperienceAPI.rejected, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.error = action.payload;
      });

    // Handle deleteExperienceAPI
    builder
      .addCase(deleteExperienceAPI.pending, (state) => {
        state.selectedStudent.loading = true;
        state.selectedStudent.error = null;
      })
      .addCase(deleteExperienceAPI.fulfilled, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.experiences =
          state.selectedStudent.experiences.filter(
            (exp) => exp.id !== action.payload.experienceId
          );
      })
      .addCase(deleteExperienceAPI.rejected, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.error = action.payload;
      });

    // Handle addEducationAPI
    builder
      .addCase(addEducationAPI.pending, (state) => {
        state.selectedStudent.loading = true;
        state.selectedStudent.error = null;
      })
      .addCase(addEducationAPI.fulfilled, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.educations = [
          action.payload.data,
          ...state.selectedStudent.educations,
        ];
      })
      .addCase(addEducationAPI.rejected, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.error = action.payload;
      });

    // Handle updateEducationAPI
    builder
      .addCase(updateEducationAPI.pending, (state) => {
        state.selectedStudent.loading = true;
        state.selectedStudent.error = null;
      })
      .addCase(updateEducationAPI.fulfilled, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.educations = state.selectedStudent.educations.map(
          (edu) =>
            edu.id === action.payload.data.id ? action.payload.data : edu
        );
      })
      .addCase(updateEducationAPI.rejected, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.error = action.payload;
      });

    // Handle deleteEducationAPI
    builder
      .addCase(deleteEducationAPI.pending, (state) => {
        state.selectedStudent.loading = true;
        state.selectedStudent.error = null;
      })
      .addCase(deleteEducationAPI.fulfilled, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.educations =
          state.selectedStudent.educations.filter(
            (edu) => edu.id !== action.payload.educationId
          );
      })
      .addCase(deleteEducationAPI.rejected, (state, action) => {
        state.selectedStudent.loading = false;
        state.selectedStudent.error = action.payload;
      });

    builder
      .addCase(adminGetAllStudentsAPI.pending, (state) => {
        state.students.loading = true;
        state.students.error = null;
      })
      .addCase(adminGetAllStudentsAPI.fulfilled, (state, action) => {
        state.students.loading = false;
        state.students.data = action.payload.data.details || [];
        state.students.totalCount = action.payload.data.totalCount || 0;
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        state.metrics.newEnrollments = state.students.data.filter(
          (student) => new Date(student.created_at) >= oneWeekAgo
        ).length;
        state.metrics.studentProgress = state.students.data.filter(
          (student) => student.status === "complete"
        ).length;
        state.metrics.activeStudents = state.students.totalCount;
      })
      .addCase(adminGetAllStudentsAPI.rejected, (state, action) => {
        state.students.loading = false;
        state.students.error = action.payload;
      });

    builder
      .addCase(adminGetAllCoursesAPI.pending, (state) => {
        state.courses.loading = true;
        state.courses.error = null;
      })
      .addCase(adminGetAllCoursesAPI.fulfilled, (state, action) => {
        state.courses.loading = false;
        state.courses.data = action.payload.data || [];
        state.courses.totalCount = action.payload.data.length || 0;
      })
      .addCase(adminGetAllCoursesAPI.rejected, (state, action) => {
        state.courses.loading = false;
        state.courses.error = action.payload;
      });

    builder
      .addCase(getAllInstructorsAPI.pending, (state) => {
        state.instructors.loading = true;
        state.instructors.error = null;
      })
      .addCase(getAllInstructorsAPI.fulfilled, (state, action) => {
        state.instructors.loading = false;
        state.instructors.data = action.payload.data || [];
      })
      .addCase(getAllInstructorsAPI.rejected, (state, action) => {
        state.instructors.loading = false;
        state.instructors.error = action.payload;
      });

    builder
      .addCase(courseAssignmentAPI.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(courseAssignmentAPI.fulfilled, (state, action) => {
        state.error = null;
        state.status = "succeeded";
      })
      .addCase(courseAssignmentAPI.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
    builder
      .addCase(updatePasswordAPI.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.lastActionType = "updatePasswordAPI";
      })
      .addCase(updatePasswordAPI.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        if (!state.selectedStudent) {
          state.selectedStudent = {};
        }
        if (!state.selectedStudent.profile) {
          state.selectedStudent.profile = {};
        }
        state.selectedStudent.id =
          action.payload.data.id || state.selectedStudent.id;
        state.selectedStudent.email =
          action.payload.data.email || state.selectedStudent.email;
      })
      .addCase(updatePasswordAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(clearNotificationsAPI.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearNotificationsAPI.fulfilled, (state) => {
        state.loading = false;
        state.notifications = [];
        state.unreadCount = 0;
      })
      .addCase(clearNotificationsAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(getEditProfileAPI.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEditProfileAPI.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedStudent = action.payload.data;
      })
      .addCase(getEditProfileAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetError, resetSelectedStudent, resetDeactivationStatus } =
  adminSlice.actions;
export default adminSlice.reducer;
